<script setup lang="ts">
import { ChevronDown, RotateCcw, X } from "lucide-vue-next";

const minValue = defineModel<string>("minValue", { required: true });
const maxValue = defineModel<string>("maxValue", { required: true });

const props = defineProps<{
  title: string;
  minLabel: string;
  maxLabel: string;
  applyText: string;
  resetText: string;
  suffix?: string;
  min?: number;
  max?: number;
  step?: number;
  integer?: boolean;
}>();

const emit = defineEmits<{
  apply: [];
}>();

const localMinValue = ref("");
const localMaxValue = ref("");

const minLimit = computed(() => props.min ?? 0);
const maxLimit = computed(() => props.max ?? 999_999);
const stepValue = computed(() => {
  if (props.step === undefined || props.step <= 0) return 1;

  return props.step;
});
const hasValue = computed(() => Boolean(minValue.value || maxValue.value));
const hasLocalValue = computed(() =>
  Boolean(localMinValue.value || localMaxValue.value),
);

const activeLabel = computed(() => {
  const suffix = props.suffix ? ` ${props.suffix}` : "";

  if (!hasValue.value) return props.title;

  if (minValue.value && maxValue.value) {
    return `${minValue.value}–${maxValue.value}${suffix}`;
  }

  if (minValue.value) return `${props.minLabel} ${minValue.value}${suffix}`;

  return `${props.maxLabel} ${maxValue.value}${suffix}`;
});

function normalizeInput(value: string | number) {
  const raw = Number(value);

  if (!Number.isFinite(raw)) return "";

  const stepped = Math.round(raw / stepValue.value) * stepValue.value;
  const bounded = Math.max(minLimit.value, Math.min(stepped, maxLimit.value));

  return String(props.integer === false ? bounded : Math.round(bounded));
}

function syncLocalValues() {
  localMinValue.value = minValue.value;
  localMaxValue.value = maxValue.value;
}

function normalizeLocalValues() {
  const nextMin = localMinValue.value
    ? normalizeInput(localMinValue.value)
    : "";
  const nextMax = localMaxValue.value
    ? normalizeInput(localMaxValue.value)
    : "";

  if (nextMin && nextMax && Number(nextMin) > Number(nextMax)) {
    localMinValue.value = nextMax;
    localMaxValue.value = nextMin;
    return;
  }

  localMinValue.value = nextMin;
  localMaxValue.value = nextMax;
}

function reset(close?: () => void) {
  minValue.value = "";
  maxValue.value = "";
  localMinValue.value = "";
  localMaxValue.value = "";
  emit("apply");
  close?.();
}

function apply(close?: () => void) {
  normalizeLocalValues();
  minValue.value = localMinValue.value;
  maxValue.value = localMaxValue.value;
  emit("apply");
  close?.();
}

watch([minValue, maxValue], syncLocalValues, { immediate: true });
</script>

<template>
  <AppDropdown
    content-class="left-0 right-auto w-[300px] min-w-[300px] max-w-[calc(100vw-32px)] !p-0"
    sheet-class="!p-0"
  >
    <template #trigger="{ open }">
      <button
        type="button"
        class="flex h-11 min-w-[192px] max-w-[224px] shrink-0 items-center justify-between gap-2 rounded-xl border px-3.5 text-left text-sm font-semibold transition duration-300"
        :class="
          open || hasValue
            ? 'border-primary/35 bg-primary-50/80 text-primary hover:bg-primary-100/70'
            : 'border-app-border bg-white text-app-text hover:border-primary/35 md:hover:bg-primary-50'
        "
        @click="syncLocalValues"
      >
        <span class="min-w-0 truncate">
          {{ activeLabel }}
        </span>

        <span
          v-if="hasValue"
          role="button"
          tabindex="0"
          class="flex size-5 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm transition duration-300 hover:bg-white"
          :aria-label="resetText"
          @click.stop="reset()"
          @keydown.enter.stop.prevent="reset()"
          @keydown.space.stop.prevent="reset()"
        >
          <X class="size-3.5" />
        </span>

        <ChevronDown
          v-else
          class="size-4 shrink-0 transition duration-300"
          :class="{ 'rotate-180': open }"
        />
      </button>
    </template>

    <template #content="{ close }">
      <div class="bg-white">
        <div
          class="flex h-14 items-center justify-between gap-3 border-b border-app-border px-4"
        >
          <h3 class="min-w-0 truncate text-sm font-bold text-app-text">
            {{ title }}
          </h3>

          <div class="flex h-8 w-[82px] shrink-0 justify-end">
            <button
              type="button"
              class="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg bg-[#F3F5F4] px-2.5 text-xs font-semibold text-app-muted transition duration-300 hover:bg-primary-50 hover:text-primary"
              :class="
                hasLocalValue
                  ? 'visible opacity-100'
                  : 'invisible pointer-events-none opacity-0'
              "
              :aria-label="resetText"
              @click="reset(close)"
            >
              <RotateCcw class="size-3.5" />
              <span>{{ resetText }}</span>
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2 p-4">
          <label class="block">
            <span class="sr-only">{{ minLabel }}</span>
            <div class="relative">
              <input
                v-model="localMinValue"
                type="text"
                inputmode="numeric"
                class="h-11 w-full rounded-lg border border-app-border bg-white px-3 pr-10 text-sm outline-none transition duration-300 placeholder:text-app-placeholder hover:border-primary/35 focus:border-primary"
                :placeholder="minLabel"
                @blur="normalizeLocalValues"
                @keydown.enter.prevent="apply(close)"
              />
              <span
                v-if="suffix"
                class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-app-muted"
              >
                {{ suffix }}
              </span>
            </div>
          </label>

          <label class="block">
            <span class="sr-only">{{ maxLabel }}</span>
            <div class="relative">
              <input
                v-model="localMaxValue"
                type="text"
                inputmode="numeric"
                class="h-11 w-full rounded-lg border border-app-border bg-white px-3 pr-10 text-sm outline-none transition duration-300 placeholder:text-app-placeholder hover:border-primary/35 focus:border-primary"
                :placeholder="maxLabel"
                @blur="normalizeLocalValues"
                @keydown.enter.prevent="apply(close)"
              />
              <span
                v-if="suffix"
                class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-app-muted"
              >
                {{ suffix }}
              </span>
            </div>
          </label>
        </div>

        <div class="border-t border-app-border p-3">
          <button
            type="button"
            class="flex h-11 w-full items-center justify-center rounded-lg bg-primary text-sm font-semibold text-white transition duration-300 hover:bg-primary-400"
            @click="apply(close)"
          >
            {{ applyText }}
          </button>
        </div>
      </div>
    </template>

    <template #mobile-content="{ close }">
      <div class="bg-white">
        <div
          class="flex h-14 items-center justify-between gap-3 border-b border-app-border px-4"
        >
          <h3 class="min-w-0 truncate text-base font-bold text-app-text">
            {{ title }}
          </h3>

          <div class="flex h-8 w-[82px] shrink-0 justify-end">
            <button
              type="button"
              class="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg bg-[#F3F5F4] px-2.5 text-xs font-semibold text-app-muted transition duration-300 active:bg-primary-50 active:text-primary"
              :class="
                hasLocalValue
                  ? 'visible opacity-100'
                  : 'invisible pointer-events-none opacity-0'
              "
              :aria-label="resetText"
              @click="reset(close)"
            >
              <RotateCcw class="size-3.5" />
              <span>{{ resetText }}</span>
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2 p-4">
          <label class="block">
            <span class="sr-only">{{ minLabel }}</span>
            <div class="relative">
              <input
                v-model="localMinValue"
                type="text"
                inputmode="numeric"
                class="h-12 w-full rounded-lg border border-app-border bg-white px-3 pr-9 text-sm outline-none transition duration-300 placeholder:text-app-placeholder focus:border-primary"
                :placeholder="minLabel"
                @blur="normalizeLocalValues"
              />
              <span
                v-if="suffix"
                class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-app-muted"
              >
                {{ suffix }}
              </span>
            </div>
          </label>

          <label class="block">
            <span class="sr-only">{{ maxLabel }}</span>
            <div class="relative">
              <input
                v-model="localMaxValue"
                type="text"
                inputmode="numeric"
                class="h-12 w-full rounded-lg border border-app-border bg-white px-3 pr-9 text-sm outline-none transition duration-300 placeholder:text-app-placeholder focus:border-primary"
                :placeholder="maxLabel"
                @blur="normalizeLocalValues"
              />
              <span
                v-if="suffix"
                class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-app-muted"
              >
                {{ suffix }}
              </span>
            </div>
          </label>
        </div>

        <div class="border-t border-app-border p-3">
          <button
            type="button"
            class="flex h-12 w-full items-center justify-center rounded-lg bg-primary text-base font-bold text-white transition duration-300 active:bg-primary-400"
            @click="apply(close)"
          >
            {{ applyText }}
          </button>
        </div>
      </div>
    </template>
  </AppDropdown>
</template>
